/**
 * Environmental years must run end to end — no gap, no overlap.
 *
 * ⚠️ THE PARSER CAN AND DOES RETURN OVERLAPPING PERIODS.
 *
 * The extraction prompt asks for a `date_range` on every certificate year, and the model
 * returns whatever the offer document appears to say. AGL's offers come back with a Year 1
 * stretched to the tender's own first year while Years 2+ stay on a plain anniversary grid:
 *
 *     SOUTH PACIFIC SEEDS, AGL, straight from parsed_offer_document:
 *       year_1  2026-10-01 -> 2027-12-31   (15 months)
 *       year_2  2027-10-01 -> 2028-09-30   (starts 3 months INSIDE year 1)
 *       year_3  2028-10-01 -> 2029-09-30
 *       year_4  2029-10-01 -> 2030-09-30
 *
 * October to December 2027 then sits in both years, so the per-year average counts that quarter
 * twice — at Year 1's rate, the dearest, because certificate prices fall year on year. Year 1 on
 * its own is a single slot and matched the client exactly; every term from Year 2 came out high:
 * 27mo 5,910 against their 5,449, 39mo 8,177 against 7,454, 51mo 10,345 against 9,300.
 *
 * ⚠️ WHY THIS LIVES HERE AND NOT IN THE SEEDING HELPER.
 *
 * An earlier attempt fixed envYearRangeFromTerms, which only runs when a REVIEWER adds or
 * renumbers a year. A normally parsed offer never reaches it, so new tenders stayed wrong. This
 * runs inside normalizeEnvironmentalCharges — the one funnel every offer's environmental data
 * passes through, on parse, on save and on review — so the shape cannot be bypassed.
 *
 * ⚠️ RATES ARE NEVER TOUCHED. Only `date_range` is rewritten. The certificate price, power
 * percentage and rate per kWh the retailer quoted are exactly what the client agreed to, and a
 * period being mis-stated says nothing about them.
 */

const MS_PER_DAY = 86_400_000;

const parseDay = (value: unknown): Date | null => {
  if (typeof value !== 'string') return null;
  const ms = Date.parse(`${value.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(ms) ? null : new Date(ms);
};

const toIso = (date: Date): string => date.toISOString().slice(0, 10);

/**
 * Add whole months, clamped to the target month's last day.
 *
 * Plain month arithmetic turns 31 Jan into 3 March. A contract year that ends on a month end
 * must stay on one, which is what every retailer quotes.
 */
const addMonths = (date: Date, months: number): Date => {
  const day = date.getUTCDate();
  const target = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return target;
};

/** Sorts year_1, year_2, … year_10 numerically rather than as text. */
const yearIndexOf = (key: string): number => {
  const m = /^year_(\d+)$/.exec(key);
  return m ? Number(m[1]) : Number.NaN;
};

/** Whichever casing this record happens to use. Parsed data carries both. */
const rangeKeyOf = (slot: Record<string, unknown>): 'date_range' | 'dateRange' | null => {
  if (slot?.date_range && typeof slot.date_range === 'object') return 'date_range';
  if (slot?.dateRange && typeof slot.dateRange === 'object') return 'dateRange';
  return null;
};

const readRange = (slot: Record<string, unknown>): { start: Date | null; end: Date | null } => {
  const key = rangeKeyOf(slot);
  if (!key) return { start: null, end: null };
  const range = slot[key] as Record<string, unknown>;
  return {
    start: parseDay(range.start_date ?? range.startDate),
    end: parseDay(range.end_date ?? range.endDate),
  };
};

const writeRange = (
  slot: Record<string, unknown>,
  start: Date,
  end: Date,
): Record<string, unknown> => {
  const key = rangeKeyOf(slot) ?? 'date_range';
  const existing = (slot[key] as Record<string, unknown>) ?? {};
  const camel = key === 'dateRange';
  return {
    ...slot,
    [key]: camel
      ? { ...existing, startDate: toIso(start), endDate: toIso(end) }
      : { ...existing, start_date: toIso(start), end_date: toIso(end) },
  };
};

/**
 * Rewrite one scheme's year periods so each begins the day after the previous one ends.
 *
 * Year 1 is taken as quoted — it is the only period the document states unambiguously, and it is
 * where the contract actually begins. Each later year keeps its own LENGTH IN MONTHS where that
 * can be read from its quoted range, so a retailer quoting an eighteen-month block keeps it;
 * otherwise it is assumed to be twelve.
 *
 * Returned unchanged when there is nothing to anchor to: no year_1, or no date on it. Inventing a
 * period nobody stated would be worse than leaving the document as it was found.
 */
export const sequenceEnvYears = <T extends Record<string, unknown>>(
  scheme: T | null | undefined,
): T => {
  if (!scheme || typeof scheme !== 'object') return scheme as T;

  const yearKeys = Object.keys(scheme)
    .filter(k => Number.isFinite(yearIndexOf(k)))
    .sort((a, b) => yearIndexOf(a) - yearIndexOf(b));
  if (yearKeys.length < 2) return scheme;

  const first = scheme[yearKeys[0]] as Record<string, unknown>;
  if (!first || typeof first !== 'object') return scheme;

  const firstRange = readRange(first);
  if (!firstRange.start || !firstRange.end) return scheme;

  // ⚠️ WHOLE-OF-TERM QUOTES ARE NOT A YEAR GRID. LEAVE THEM EXACTLY AS THEY ARE.
  //
  // AGL does not quote a rate per year. It quotes one already-blended rate per contract
  // LENGTH, so every window runs from the same start and simply ends later:
  //
  //     BEDELIS INVESTMENTS, AGL, straight from parsed_offer_document:
  //       year_1  2027-02-01 -> 2028-01-31   (the 1-year deal)
  //       year_2  2027-02-01 -> 2029-01-31   (the 2-year deal, already an average)
  //       year_3  2027-02-01 -> 2030-01-31   (the 3-year deal)
  //       year_4  2027-02-01 -> 2031-01-31   (the 4-year deal)
  //
  // Those windows are MEANT to sit on top of each other, and the shared start is the only
  // thing that says so — calculationService's detectTermQuotedEnv reads precisely this shape
  // to decide that a term reads ONE slot instead of averaging across them.
  //
  // Sequencing pushed each start past the previous end, which erased the shared start. The
  // offer was then read as successive years and every term above the first averaged one
  // average into the next, dragging in year_1's dearest certificates: BEDELIS went out at
  // 4,722 / 7,038 / 9,355 against the client's 4,597 / 6,943 / 9,260. Only the 12-month
  // term — the one that reads year_1 on its own either way — still agreed to the dollar,
  // which is exactly how the defect was reported.
  //
  // A shared start with a shared END is not a term quote; it is a parser that stamped one
  // range onto every slot, and those still want sequencing. Both halves are required, the
  // same way detectTermQuotedEnv requires them.
  const quoted = yearKeys.map(key => readRange(scheme[key] as Record<string, unknown>));
  if (quoted.every(r => r.start && r.end)) {
    const sameStart = quoted.every(r => r.start!.getTime() === quoted[0].start!.getTime());
    const endsDiffer = new Set(quoted.map(r => r.end!.getTime())).size > 1;
    if (sameStart && endsDiffer) return scheme;
  }

  const out: Record<string, unknown> = { ...scheme };
  let previousEnd = firstRange.end;

  for (const key of yearKeys.slice(1)) {
    const slot = scheme[key] as Record<string, unknown>;
    if (!slot || typeof slot !== 'object') continue;

    const { start, end } = readRange(slot);
    // Keep the quoted length when it is readable, so a non-twelve-month block survives.
    const months = start && end
      ? Math.max(1, Math.round((end.getTime() - start.getTime()) / MS_PER_DAY / 30.436875))
      : 12;

    const nextStart = new Date(previousEnd.getTime() + MS_PER_DAY);
    const nextEnd = new Date(addMonths(nextStart, months).getTime() - MS_PER_DAY);

    out[key] = writeRange(slot, nextStart, nextEnd);
    previousEnd = nextEnd;
  }

  return out as T;
};

/** Apply the sequencing to every scheme of an environmental-charges record. */
export const sequenceEnvCharges = <T extends Record<string, unknown>>(
  charges: T | null | undefined,
): T => {
  if (!charges || typeof charges !== 'object') return charges as T;
  const out: Record<string, unknown> = {};
  for (const [schemeKey, scheme] of Object.entries(charges)) {
    out[schemeKey] = scheme && typeof scheme === 'object'
      ? sequenceEnvYears(scheme as Record<string, unknown>)
      : scheme;
  }
  return out as T;
};
